/**
 * usePasskey Hook
 * @description Passkey 등록 및 인증을 위한 커스텀 훅
 */

import { useState } from 'react';

function extractErrorMessage(err: unknown, fallback: string): string {
  if (err instanceof Error) {
    const msg = (err as Error & { response?: { message?: string } }).response?.message;
    if (msg) return msg;
    return err.message || fallback;
  }
  return fallback;
}
import {
  registerPasskey,
  authenticateWithPasskey,
  getPasskeys,
  deletePasskey,
  type Passkey,
} from '@/services/passkeyApi';

export function usePasskey() {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Passkey 등록
   */
  const register = async (adminUserId: string): Promise<string> => {
    setIsLoading(true);
    setError(null);
    try {
      const passkeyId = await registerPasskey(adminUserId);
      return passkeyId;
    } catch (err) {
      const message = extractErrorMessage(err, 'Passkey 등록에 실패했습니다.');
      setError(message);
      throw new Error(message);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Passkey 인증
   */
  const authenticate = async (adminUserId: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await authenticateWithPasskey(adminUserId);
      return result;
    } catch (err) {
      const message = extractErrorMessage(err, 'Passkey 인증에 실패했습니다.');
      setError(message);
      throw new Error(message);
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * 등록된 Passkey 목록 조회
   */
  const fetchPasskeys = async (): Promise<Passkey[]> => {
    setIsLoading(true);
    setError(null);
    try {
      const result = await getPasskeys();
      return result.passkeys;
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Passkey 목록을 불러오는데 실패했습니다.';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Passkey 삭제
   */
  const remove = async (passkeyId: string): Promise<void> => {
    setIsLoading(true);
    setError(null);
    try {
      await deletePasskey(passkeyId);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Passkey 삭제에 실패했습니다.';
      setError(message);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    register,
    authenticate,
    fetchPasskeys,
    remove,
    isLoading,
    error,
  };
}
